import { useCallback, useState } from 'react';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import { usePageCRUDActions } from '@mm/state/actions/PageCRUDActions';
import WidgetModel, { WidgetTypes } from '@mm/state/model/WidgetModel';
import { useSelector } from 'react-redux';
import { StoreState } from '@mm/state/store-interfaces';
import { pageEntityByIdSelector } from '@mm/state/selectors/pageSelectors';
import PageModel from '@mm/state/model/PageModel';

export const getWidgetPageMap = (
  widget: WidgetModel,
): Record<string, number> => {
  switch (widget.type) {
    case WidgetTypes.NAVIGATE: {
      if (!widget.config?.oppositePageId || !widget.config?.pageId) {
        return {};
      }

      return {
        yes: widget.config?.pageId,
        no: widget.config?.oppositePageId,
      };
    }
    default:
      return {};
  }
};

export const getDialogueIds = (widgets?: WidgetModel[]): number[] => {
  if (Array.isArray(widgets)) {
    return widgets.map(getWidgetPageMap).map(Object.values).flat();
  }

  return [];
};

const asyncTraverse = ({
  head,
  getChildren,
}: {
  head: number;
  getChildren: (id: number) => Promise<number[]>;
}) => {
  let visited: number[] = [];

  const go = async (id: number) => {
    if (!id || visited.includes(id)) {
      return;
    }

    const ids = await getChildren(id);
    visited = [...visited, id];

    const filtered = ids.filter(v => !visited.includes(v));

    const all = filtered.map(v => go(v));

    await Promise.all(all);
  };

  return go(head);
};

export const useDialogues = () => {
  const store = useSelector<StoreState, StoreState>(store => store);
  const widgetCRUDActions = useWidgetCRUDActions();
  const pageCRUDActions = usePageCRUDActions();

  const get = useCallback(
    (dialogueId: number) => {
      return pageEntityByIdSelector(store, dialogueId.toString());
    },
    [store],
  );

  const fetch = useCallback(async (dialogueId: number) => {
    try {
      const [widgets, dialogue] = await Promise.all([
        widgetCRUDActions.retrieveAll(dialogueId, true),
        pageCRUDActions.retrieveById(dialogueId),
      ]);
      return { widgets, dialogue };
    } catch (err) {
      return { widgets: [] as WidgetModel[], dialogue: undefined };
    }
  }, []);

  return { get, fetch };
};

export const useFetchDialogue = () => {
  const pageCRUDActions = usePageCRUDActions();

  const [state, setState] = useState<'ready' | 'fetching' | 'error' | ''>('');

  const dialogues = useDialogues();

  const getDialogueWidgets = async (dialogueId: number, data: PageModel[]) => {
    const dialogue =
      data.find(p => p.id === dialogueId) || dialogues.get(dialogueId);

    if (dialogue && dialogue.widgets) {
      console.debug(`use-fetch-dialogue: found cache ${dialogueId}`);
      return dialogue.widgets;
    }

    console.debug(`use-fetch-dialogue: fetching ${dialogueId}`);
    const res = await dialogues.fetch(dialogueId);
    return res.widgets;
  };

  const run = async (dialogueId: number, pageGroupId?: number) => {
    // constrain: first dialogue has master page group id
    let data = [];

    if (pageGroupId) {
      // get all pages by groupId to retrieve them from store later
      data = await pageCRUDActions.retrievePagesByGroupId({
        pageGroupId,
        showWidgets: true,
      });
    }

    await asyncTraverse({
      head: dialogueId,
      getChildren: async id => {
        const widgets = await getDialogueWidgets(id, data);
        return getDialogueIds(widgets || []);
      },
    });
  };

  const init = async (dialogueId: number, pageGroupId?: number) => {
    setState('fetching');

    try {
      await run(dialogueId, pageGroupId);
      setState('ready');
    } catch (err) {
      setState('error');
    }
  };

  return {
    state,
    init,
    run,
  };
};
